import { EDiscountType } from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
} from 'antd';
import { type Dayjs } from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { crmCustomerCouponCreate } from '../../reducers/crm-customer-detail/reducer';
import { discountTypesLoad } from '../../reducers/discount/reducer';
import { SPACING } from '../../theme/tokens';

interface ICustomerCouponFormValues {
  discountTypeUuid: string;
  value: number;
  description: string;
  code?: string;
  validTo?: Dayjs;
}

const DISCOUNT_TYPE_LABEL: Partial<Record<string, string>> = {
  [EDiscountType.PERCENTAGE]: 'Percentage',
  [EDiscountType.AMOUNT]: 'Fixed amount',
  [EDiscountType.SHIPPING]: 'Shipping',
};

const VALUE_LABEL: Partial<Record<string, string>> = {
  [EDiscountType.PERCENTAGE]: 'Discount in %',
  [EDiscountType.AMOUNT]: 'Discount amount',
  [EDiscountType.SHIPPING]: 'Discount on shipping in %',
};

function CustomerCouponModal({
  open,
  onClose,
  customerUUID,
  customerEmail,
  inProcess,
}: {
  open: boolean;
  onClose: () => void;
  customerUUID: string;
  customerEmail: string | undefined;
  inProcess: boolean;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const discountTypes = useSelector((s: RootState) => s.Discount.discountTypes);
  const [form] = Form.useForm<ICustomerCouponFormValues>();
  const discountTypeUuidWatch: string | undefined = Form.useWatch(
    'discountTypeUuid',
    form,
  );
  const selectableDiscountTypes = discountTypes?.filter(
    (discountType): boolean => discountType.code !== EDiscountType.REFERRAL,
  );
  const defaultDiscountTypeUuid: string | undefined = discountTypes?.find(
    (discountType): boolean => discountType.code === EDiscountType.PERCENTAGE,
  )?.uuid;
  const selectedDiscountTypeCode: string | undefined = discountTypes?.find(
    (discountType): boolean => discountType.uuid === discountTypeUuidWatch,
  )?.code;
  const valueLabel: string =
    (selectedDiscountTypeCode
      ? VALUE_LABEL[selectedDiscountTypeCode]
      : undefined) ?? 'Value';

  useEffect(() => {
    if (open && !discountTypes) {
      dispatch(discountTypesLoad());
    }
  }, [open, discountTypes, dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      discountTypeUuid: defaultDiscountTypeUuid,
      description: customerEmail ? `Coupon for ${customerEmail}` : '',
    });
  }, [open, defaultDiscountTypeUuid, customerEmail, form]);

  const handleFinish = (values: ICustomerCouponFormValues): void => {
    dispatch(
      crmCustomerCouponCreate(customerUUID, {
        discountType: { uuid: values.discountTypeUuid },
        value: values.value,
        description: values.description,
        code: values.code || undefined,
        validTo: values.validTo ? values.validTo.endOf('day').toDate() : null,
      }),
    );
    onClose();
  };

  return (
    <Modal
      title="New coupon for this customer"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <Alert
        type={customerEmail ? 'info' : 'warning'}
        showIcon
        style={{ marginBottom: SPACING.lg }}
        title="One-time coupon that only this customer can use."
        description={
          customerEmail
            ? `The code will be emailed to ${customerEmail}.`
            : 'The customer has no account email, so the code will not be emailed.'
        }
      />

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="discountTypeUuid"
          label="Discount type"
          rules={[{ required: true }]}
        >
          <Radio.Group
            optionType="button"
            disabled={inProcess}
            options={selectableDiscountTypes?.map((discountType) => ({
              value: discountType.uuid,
              label:
                DISCOUNT_TYPE_LABEL[discountType.code] ?? discountType.code,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="value"
          label={valueLabel}
          rules={[{ required: true }, { type: 'number', min: 0 }]}
        >
          <InputNumber style={{ width: '100%' }} min={0} disabled={inProcess} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true }, { type: 'string', min: 3 }]}
        >
          <Input disabled={inProcess} />
        </Form.Item>

        <Form.Item
          name="validTo"
          label="Valid to (leave empty to never expire)"
        >
          <DatePicker style={{ width: '100%' }} disabled={inProcess} />
        </Form.Item>

        <Form.Item name="code" label="Custom code (optional)">
          <Input placeholder="Generated automatically" disabled={inProcess} />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          disabled={inProcess}
          loading={inProcess}
        >
          {customerEmail ? 'Create and send' : 'Create'}
        </Button>
      </Form>
    </Modal>
  );
}

export default CustomerCouponModal;
